/**
 * Tenant Context Service
 *
 * Verwaltet den aktuellen Tenant- und Location-Kontext fuer Multi-Tenant-Anwendungen.
 * Verwendet Angular Signals fuer reaktive State-Verwaltung und BehaviorSubject fuer Legacy-Code.
 *
 * Features:
 * - Signal-basierter State (Angular 20)
 * - Computed Signals fuer abgeleitete Werte
 * - Observable fuer Legacy-Kompatibilitaet
 * - LocalStorage-Persistierung
 * - Permission-Checks
 */

import { Injectable, signal, computed } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Tenant Context Interface
 * Definiert den aktuellen Kontext fuer Multi-Tenant-Zugriffe
 */
export interface TenantContext {
  /** Tenant ID (ehemals companyId) */
  tenantId: string | null;

  /** Tenant Name (fuer Anzeige) */
  tenantName: string | null;

  /** Aktuelle Location ID */
  locationId: string | null;

  /** Location Name (fuer Anzeige) */
  locationName: string | null;

  /** User-Rolle im aktuellen Tenant */
  role: 'owner' | 'admin' | 'manager' | 'cashier' | 'viewer' | null;

  /** User-Permissions im aktuellen Tenant */
  permissions: string[];

  /** Alle Location IDs zu denen User Zugriff hat */
  locationIds: string[];
}

@Injectable({
  providedIn: 'root'
})
export class TenantContextService {
  // Signals (Angular 20)
  private contextSignal = signal<TenantContext>({
    tenantId: null,
    tenantName: null,
    locationId: null,
    locationName: null,
    role: null,
    permissions: [],
    locationIds: []
  });

  // Computed Signals
  readonly tenantId = computed(() => this.contextSignal().tenantId);
  readonly tenantName = computed(() => this.contextSignal().tenantName);
  readonly locationId = computed(() => this.contextSignal().locationId);
  readonly locationName = computed(() => this.contextSignal().locationName);
  readonly role = computed(() => this.contextSignal().role);
  readonly isOwner = computed(() => this.contextSignal().role === 'owner');
  readonly isAdmin = computed(() => this.contextSignal().role === 'admin');
  readonly isManager = computed(() => this.contextSignal().role === 'manager');
  readonly hasMultipleLocations = computed(() =>
    (this.contextSignal().locationIds?.length || 0) > 1
  );

  // Observable fuer Legacy-Code
  private contextSubject = new BehaviorSubject<TenantContext>({
    tenantId: null,
    tenantName: null,
    locationId: null,
    locationName: null,
    role: null,
    permissions: [],
    locationIds: []
  });

  readonly context$ = this.contextSubject.asObservable();

  /**
   * Setzt den gesamten Context
   */
  setContext(context: Partial<TenantContext>): void {
    const newContext = {
      ...this.contextSignal(),
      ...context
    };
    this.contextSignal.set(newContext);
    this.contextSubject.next(newContext);

    // Persist to localStorage
    localStorage.setItem('tenantContext', JSON.stringify(newContext));
  }

  /**
   * Setzt Tenant-Informationen
   */
  setTenant(tenantId: string, tenantName: string): void {
    this.setContext({ tenantId, tenantName });
  }

  /**
   * Setzt Location-Informationen
   */
  setLocation(locationId: string, locationName: string): void {
    this.setContext({ locationId, locationName });
  }

  /**
   * Loescht den gesamten Context
   */
  clearContext(): void {
    const emptyContext: TenantContext = {
      tenantId: null,
      tenantName: null,
      locationId: null,
      locationName: null,
      role: null,
      permissions: [],
      locationIds: []
    };
    this.contextSignal.set(emptyContext);
    this.contextSubject.next(emptyContext);
    localStorage.removeItem('tenantContext');
  }

  /**
   * Laedt Context aus localStorage
   */
  loadFromStorage(): void {
    const stored = localStorage.getItem('tenantContext');
    if (stored) {
      try {
        const context = JSON.parse(stored);
        this.contextSignal.set(context);
        this.contextSubject.next(context);
      } catch (e) {
        console.error('Failed to load tenant context', e);
      }
    }
  }

  /**
   * Prueft ob User eine bestimmte Permission hat
   */
  hasPermission(permission: string): boolean {
    const perms = this.contextSignal().permissions;
    return perms.includes('all') || perms.includes(permission);
  }
}
